/**
 * SheetTicketLink — on a day sheet, the field ticket the sheet is on.
 *
 * The API's sheet read carries no ticket id (measured at api 42ff0fc0), so the
 * link is DERIVED from two reads the ticket roles already have:
 *
 *   1. GET /tickets?job_id=<the sheet's job>, then the one live (non-void)
 *      candidate: a per-day ticket for the sheet's UNIT and work date (a
 *      per-day ticket is per unit per day), or the job's whole-job ticket.
 *   2. GET /tickets/:id for that candidate. The link shows ONLY when the
 *      ticket's `sheets` lists this sheet with `releasedAt` empty — the API's
 *      own record that the sheet is on it. A candidate that does not list the
 *      sheet (a sheet approved after the draft, or one a refresh released)
 *      shows nothing rather than a guess.
 *
 * Rendered for the ticket READ roles only; the reads are not made otherwise.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { useWlTicket, useWlTickets } from '../../../hooks/useWlTickets';
import { calendarDayString } from '../../../lib/calendar-date';
import type { WlTicket } from '../../../types/wireline-slickline-tickets';
import { WlPanel } from '../WlBits';

const day = (v: string | null | undefined) => calendarDayString(v);

/** The live ticket a sheet could be on, by scope. Exported for the tests. */
export function sheetTicketCandidate(tickets: WlTicket[], sheet: { workDate: string | null; unitId: string | null }): WlTicket | null {
  const live = tickets.filter((t) => t.status !== 'void');
  const wd = day(sheet.workDate);
  return (
    live.find((t) => t.scope === 'day' && wd !== null && day(t.workDate) === wd && (!t.unitId || !sheet.unitId || t.unitId === sheet.unitId)) ??
    live.find((t) => t.scope === 'job') ??
    null
  );
}

/** Whether a ticket's detail lists this sheet as on it (not released). */
export function ticketHoldsSheet(ticket: WlTicket | null | undefined, sheetId: string): boolean {
  return !!ticket?.sheets?.some((s) => s.id === sheetId && !s.releasedAt);
}

export function WlSheetTicketLink({ sheetId, jobId, workDate, unitId }: { sheetId: string; jobId: string; workDate: string | null; unitId: string | null }) {
  const list = useWlTickets({ job_id: jobId });
  const candidate = sheetTicketCandidate(list.data?.data ?? [], { workDate, unitId });
  const detail = useWlTicket(candidate?.id);
  const ticket = detail.data?.data ?? null;
  if (!ticket || !ticketHoldsSheet(ticket, sheetId)) return null;
  return (
    <WlPanel eyebrow="Billing" title="Field ticket">
      <p className="px-4 py-3 text-sm text-fieldiq-text" data-testid="wl-sheet-ticket">
        This day sheet is on{' '}
        <Link to={`/wireline-slickline/tickets/${ticket.id}`} className="font-mono font-bold text-fi-amber hover:underline">
          {ticket.ticketNumber || '—'}
        </Link>
        .
      </p>
    </WlPanel>
  );
}
